// src/lib/portal.ts — the portal's own pages beyond Home (Profile, Notifications), on the same endpoints the phone app
// calls (mobile/src/lib/profile.ts, mobile/src/lib/notifications.ts):
//   GET /my-profile              the person's account, booking details, screening and documents (own records only)
//   PATCH /auth/me/profile       their name and mobile (the sign-up rules; the server has the final word)
//   GET /notifications           their last 50, with meta.unread
//   POST /notifications/read     { ids } — or everything
// The password changes through the auth store's changePassword (POST /auth/change-password), as on every other screen.
import { api } from './api';
import type { AuthUser } from '../types/api';
import { phoneOk, phoneError as phoneMessageFor } from './phone';

export interface ProfileScreening {
  status: 'none' | 'sent' | 'passed' | 'revoked';
  employer: string | null; jobTitle: string | null; annualIncomeMinor: number | null; currency: string;
  maxRentMinor: number | null; multiple: number;
  submittedAt: string | null; approvedAt: string | null; revokedAt: string | null;
}
export interface ProfileDocument { id: string; category: string; categoryLabel: string; name: string; uploadedAt: string; url: string | null; screeningIndex: number | null }
export interface Profile {
  account: { firstName: string | null; lastName: string | null; email: string; phone: string | null };
  details: { dateOfBirth?: string | null; currentAddress?: string | null; occupants?: Array<{ name: string }>; startDate: string | null } | null;
  screening: ProfileScreening;
  documents: ProfileDocument[];
  booking: { flat: string; property: string; done: number; total: number; rentMinor: number; currency: string; status: string } | null;
}

export const profileApi = {
  get: async () => (await api.get<Profile>('/my-profile')).data,
  update: async (body: { firstName: string; lastName: string; phone: string }) => (await api.patch<AuthUser>('/auth/me/profile', body)).data,
};

export interface PortalNotification {
  id: string; title: string; body: string | null; category: string | null;
  resource: string | null; resourceId: string | null; url: string | null; readAt: string | null; createdAt: string;
}
export const NOTIFICATIONS_KEY = ['/notifications'] as const;
export const notificationsApi = {
  list: async () => {
    const r = await api.get<PortalNotification[]>('/notifications');
    return { items: r.data ?? [], unread: Number((r.meta as { unread?: number } | undefined)?.unread ?? 0) };
  },
  read: async (ids?: string[]) => { await api.post('/notifications/read', ids ? { ids } : {}); },
};

/** The phone app's paths (a notification written for the app names one) → the portal's page. */
const APP_TO_WEB: Record<string, string> = {
  '/': '/app/my-home', '/booking': '/app/my-booking', '/booking/sign': '/app/my-booking', '/account': '/app/profile', '/profile/edit': '/app/profile',
  '/resident/rent': '/app/my-home/rent', '/resident/repairs': '/app/my-home/repairs', '/resident/documents': '/app/my-home/documents', '/messages': '/app/messages',
};
/** Portal pages a notification may name directly; anything else (a staff screen) is not followed. */
const PORTAL = /^\/app\/(my-home|my-booking|messages|profile|notifications)(\/|\?|$)/;

/** Where a notification opens in the portal, or null when it is only news (the app's routeFor). */
export function portalRouteFor(n: PortalNotification, kind: 'resident' | 'applicant'): string | null {
  if (n.resource === 'conversations' && n.resourceId) return `/app/messages?c=${n.resourceId}`;
  if (n.url && APP_TO_WEB[n.url]) return n.url === '/' && kind === 'applicant' ? '/app/my-booking' : APP_TO_WEB[n.url]!;
  if (n.url && PORTAL.test(n.url)) return n.url;
  switch (n.resource) {
    case 'reservations': return '/app/my-booking';
    case 'work_orders': return '/app/my-home/repairs';
    case 'documents': return '/app/my-home/documents';
    case 'screenings': case 'users': return '/app/profile';
    case 'tenancies': return n.category === 'RENT' ? '/app/my-home/rent' : kind === 'resident' ? '/app/my-home' : '/app/my-booking';
    default: return null;
  }
}

/** "just now", "5 min ago", "3 h ago", "yesterday", "4 days ago", then the date (the app's relativeTime). */
export function relativeTime(iso: string, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d} days ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: now - new Date(iso).getTime() > 300 * 86_400_000 ? 'numeric' : undefined });
}

// ─── The sign-up rules, checked as the person types (mobile/src/lib/validation.ts; the server has the final word) ───

/** The mobile is valid for its country, picked in the field's dropdown (components/ui/PhoneInput) and kept as E.164. */
export function phoneError(phone: string): string | null {
  if (!phone.trim()) return 'Put in your mobile number.';
  return phoneOk(phone) ? null : `${phoneMessageFor(phone)}.`;
}
